<template>
  <div class="workflow-payload-node"
    :class="{ 'workflow-payload-node--root': depth === 0, 'workflow-payload-node--nested': depth > 0 }">
    <v-sheet class="workflow-payload-node__row" :class="{ 'workflow-payload-node__row--container': isContainer }"
      variant="outlined" rounded>
      <v-row align="start" class="ma-0" dense>
        <v-col v-if="parentType === 'object'" cols="3" class="py-1">
          <div class="workflow-payload-node__key-cell">
            <v-btn v-if="isContainer" :icon="node.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-down'"
              variant="text" density="compact" size="small" @click="toggleCollapsed" />
            <span v-else class="workflow-payload-node__key-spacer" />
            <v-text-field :model-value="node.key" @update:model-value="updateKey" label="Key" density="compact"
              variant="outlined" :rules="keyRules" :error-messages="duplicateKeyErrors" />
          </div>
        </v-col>

        <v-col v-else cols="3" class="py-1">
          <div class="workflow-payload-node__array-index">
            <v-btn v-if="isContainer" :icon="node.collapsed ? 'mdi-chevron-right' : 'mdi-chevron-down'"
              variant="text" density="compact" size="small" @click="toggleCollapsed" />
            <span v-else class="workflow-payload-node__key-spacer" />
            <v-icon icon="mdi-drag-horizontal" size="small" />
            <v-chip size="x-small" density="compact" variant="tonal">{{ index }}</v-chip>
          </div>
          <div class="workflow-payload-node__details-spacer" />
        </v-col>

        <v-col cols="2" class="py-1">
          <v-select :model-value="node.type" @update:model-value="updateType" :items="typeItems"
            item-title="title" item-value="value" density="compact" variant="outlined">
            <template #selection="{ item }">
              <div class="workflow-payload-node__type-label">
                <v-icon :icon="item.raw.icon" size="small" />
                <span>{{ item.raw.title }}</span>
              </div>
            </template>
            <template #item="{ props, item }">
              <v-list-item v-bind="props" :prepend-icon="item.raw.icon" :title="item.raw.title" />
            </template>
          </v-select>
        </v-col>

        <v-col class="py-1">
          <v-text-field v-if="node.type === 'string'" :model-value="node.value" @update:model-value="updateValue"
            density="compact" variant="outlined" />

          <v-text-field v-else-if="node.type === 'number'" :model-value="node.value"
            @update:model-value="updateValue" density="compact" variant="outlined" :rules="numberRules" />

          <div v-else-if="node.type === 'boolean'">
            <v-btn-toggle :model-value="node.value" mandatory density="compact" variant="outlined"
              :class="{ 'workflow-payload-node__toggle--error': valueErrors.length > 0 }"
              @update:model-value="updateValue">
              <v-btn :value="true">Yes</v-btn>
              <v-btn :value="false">No</v-btn>
            </v-btn-toggle>
            <div class="workflow-payload-node__messages" :class="{ 'workflow-payload-node__messages--error': valueErrors.length > 0 }">
              {{ valueErrors[0] || '' }}
            </div>
          </div>
        </v-col>

        <v-col cols="auto" class="py-1">
          <div class="workflow-payload-node__actions">
            <v-btn v-if="isContainer" icon="mdi-plus" variant="text" density="compact" size="small"
              @click="addChild" />
            <v-btn v-if="parentType === 'array'" icon="mdi-arrow-up" variant="text" density="compact" size="small"
              :disabled="!canMoveUp" @click="$emit('move-up')" />
            <v-btn v-if="parentType === 'array'" icon="mdi-arrow-down" variant="text" density="compact" size="small"
              :disabled="!canMoveDown" @click="$emit('move-down')" />
            <v-btn icon="mdi-delete-outline" variant="text" density="compact" size="small" color="error"
              @click="$emit('delete')" />
          </div>
          <div class="workflow-payload-node__details-spacer" />
        </v-col>
      </v-row>
    </v-sheet>

    <div v-if="isContainer && !node.collapsed" class="workflow-payload-node__children">
      <WorkflowPayloadNode v-for="(child, childIndex) in node.children" :key="child.id" :node="child"
        :parent-type="node.type === 'array' ? 'array' : 'object'" :index="childIndex" :depth="depth + 1"
        :validation-issues="validationIssues" :can-move-up="childIndex > 0"
        :can-move-down="childIndex < node.children.length - 1" @update="emitUpdate"
        @delete="deleteChild(childIndex)" @move-up="moveChild(childIndex, childIndex - 1)"
        @move-down="moveChild(childIndex, childIndex + 1)" />

      <v-btn class="workflow-payload-node__add-child" prepend-icon="mdi-plus" variant="outlined" density="compact"
        @click="addChild">
        {{ node.type === 'array' ? 'Add Item' : 'Add Field' }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component'
import type { PropType } from 'vue'
import {
  addPayloadChild,
  movePayloadNode,
  PAYLOAD_TYPE_ITEMS,
  PayloadNode,
  PayloadNodeType,
  PayloadParentType,
  PayloadValidationIssue,
  setPayloadNodeType,
  validationMessagesForNode
} from '@/utils/workflowPayloadTree'

@Options({
  name: 'WorkflowPayloadNode',
  props: {
    node: { type: Object as PropType<PayloadNode>, required: true },
    parentType: { type: String as PropType<PayloadParentType>, required: true },
    index: { type: Number, required: true },
    depth: { type: Number, default: 0 },
    validationIssues: { type: Array as PropType<PayloadValidationIssue[]>, default: () => [] },
    canMoveUp: { type: Boolean, default: false },
    canMoveDown: { type: Boolean, default: false }
  },
  emits: ['update', 'delete', 'move-up', 'move-down']
})
export default class WorkflowPayloadNode extends Vue {
  node!: PayloadNode
  parentType!: PayloadParentType
  index!: number
  depth!: number
  validationIssues!: PayloadValidationIssue[]
  canMoveUp!: boolean
  canMoveDown!: boolean

  get typeItems() {
    return PAYLOAD_TYPE_ITEMS
  }

  get isContainer(): boolean {
    return this.node.type === 'object' || this.node.type === 'array'
  }

  get duplicateKeyErrors(): string[] {
    return validationMessagesForNode(this.validationIssues, this.node.id, 'key')
      .filter((message) => message !== 'Key is required')
  }

  get valueErrors(): string[] {
    return validationMessagesForNode(this.validationIssues, this.node.id, 'value')
  }

  get keyRules() {
    return [
      (value: string) => this.parentType !== 'object' || !!String(value || '').trim() || 'Key is required'
    ]
  }

  get numberRules() {
    return [
      (value: string | number) => {
        const stringValue = String(value ?? '').trim()
        return stringValue.length > 0 && Number.isFinite(Number(stringValue)) || 'Enter a valid number'
      }
    ]
  }

  updateKey(value: string) {
    this.node.key = value
    this.emitUpdate()
  }

  updateValue(value: string | number | boolean | null) {
    this.node.value = value
    this.emitUpdate()
  }

  updateType(value: PayloadNodeType) {
    Object.assign(this.node, setPayloadNodeType(this.node, value))
    this.emitUpdate()
  }

  addChild() {
    addPayloadChild(this.node)
    this.emitUpdate()
  }

  deleteChild(index: number) {
    this.node.children.splice(index, 1)
    this.emitUpdate()
  }

  moveChild(fromIndex: number, toIndex: number) {
    movePayloadNode(this.node.children, fromIndex, toIndex)
    this.emitUpdate()
  }

  toggleCollapsed() {
    this.node.collapsed = !this.node.collapsed
    this.emitUpdate()
  }

  emitUpdate() {
    this.$emit('update')
  }
}
</script>

<style lang="scss" scoped>
.workflow-payload-node {
  margin-bottom: 10px;
}

.workflow-payload-node--nested {
  margin-left: 20px;
}

.workflow-payload-node--root .workflow-payload-node__key-cell {
  gap: 6px;
}

.workflow-payload-node--root .workflow-payload-node__key-cell > :deep(.v-btn) {
  width: 24px;
  min-width: 24px;
}

.workflow-payload-node--root .workflow-payload-node__key-spacer {
  width: 24px;
  flex-basis: 24px;
}

.workflow-payload-node__row {
  border-color: transparent;
  background: transparent;
}

.workflow-payload-node__row--container {
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-surface), 0.18);
}

.workflow-payload-node__array-index,
.workflow-payload-node__key-cell,
.workflow-payload-node__type-label,
.workflow-payload-node__empty-value,
.workflow-payload-node__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.workflow-payload-node__array-index {
  min-height: 40px;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.workflow-payload-node__key-spacer {
  width: 28px;
  flex: 0 0 28px;
}

.workflow-payload-node__key-cell :deep(.v-input) {
  flex: 1 1 auto;
  min-width: 0;
}

.workflow-payload-node__empty-value {
  min-height: 40px;
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-style: italic;
}

.workflow-payload-node__details-spacer {
  min-height: 22px;
}

.workflow-payload-node__messages {
  min-height: 22px;
  padding-inline-start: 16px;
  padding-top: 4px;
  font-size: 0.75rem;
  line-height: 1.25rem;
}

.workflow-payload-node__messages--error {
  color: rgb(var(--v-theme-error));
}

.workflow-payload-node__toggle--error {
  border: 1px solid rgb(var(--v-theme-error));
  border-radius: 4px;
}

.workflow-payload-node__children {
  margin-left: 16px;
  padding-left: 20px;
  border-left: 1px solid rgba(var(--v-border-color), 0.32);
}

.workflow-payload-node__add-child {
  width: 100%;
  justify-content: flex-start;
  border-style: dashed;
}
</style>
